import { doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { viewsForSetup } from '../utils/onboardingPresets'
import type { ViewsForSetupResult } from '../utils/onboardingPresets'
import { modulesForUseCase, type ExtendedWorkspaceUseCase } from '../utils/workspaceModules'
import {
  createDefaultWorkspaceNotificationPreferences,
  saveWorkspaceNotificationPreferences,
} from './notificationService'

export type OnboardingSetupKind = 'personal' | 'team' | 'PERSONAL' | 'TEAM'

export interface WorkspaceOnboardingDraft {
  workspaceName?: string
  workspaceSlug?: string
  isPublic?: boolean
  currency?: string
  roleTitle?: string | null
  useCase?: ExtendedWorkspaceUseCase | '' | null
  teamSize?: string | null
  audience?: 'SOLO' | 'SOLO_HELPERS' | 'TEAM' | null
  intentText?: string | null
  focusAreas?: string[] | null
  theme?: string | null
  density?: string | null
  aiSuggestionsEnabled?: boolean
  notificationsEnabled?: boolean
  gamificationEnabled?: boolean
}

/**
 * Applies onboarding setup for a newly created workspace using dotted paths (Plan OC18 + OC1.a).
 * - Writes `onboardingSetup` as a whole object.
 * - Writes `workspacePreferences.aiSuggestionsEnabled`, `workspacePreferences.notificationsEnabled`,
 *   and `workspacePreferences.gamificationEnabled: true` by dotted path, preserving
 *   existing keys like `workspacePreferences.checklistBlockedTodoPolicy`.
 * - Writes `enabledDashboardViews` and `defaultDashboardView` computed by `viewsForSetup`.
 * - Sets the workspace notification master preference via `saveWorkspaceNotificationPreferences`.
 */
export async function applyOnboardingSetup(
  workspaceId: string,
  draft: WorkspaceOnboardingDraft,
  kind: OnboardingSetupKind = 'team',
): Promise<ViewsForSetupResult> {
  const isPersonal = String(kind).toLowerCase() === 'personal'
  const views = viewsForSetup(draft.useCase, isPersonal ? 'SOLO' : draft.audience)
  const modules = isPersonal
    ? modulesForUseCase(null)
    : modulesForUseCase(draft.useCase, draft.audience)

  const onboardingSetupPayload = {
    version: 1,
    roleTitle: isPersonal ? null : (draft.roleTitle || null),
    useCase: draft.useCase || null,
    teamSize: isPersonal ? 'SOLO' : (draft.teamSize || null),
    audience: isPersonal ? 'SOLO' : (draft.audience || null),
    intentText: draft.intentText || null,
    focusAreas: draft.focusAreas || [],
    preferences: {
      theme: draft.theme || 'SYSTEM',
      aiSuggestionsEnabled: draft.aiSuggestionsEnabled ?? true,
      notificationsEnabled: draft.notificationsEnabled ?? true,
      gamificationEnabled: true,
    },
    completedAt: serverTimestamp(),
  }

  await updateDoc(doc(firestoreDb, 'workspaces', workspaceId), {
    onboardingSetup: onboardingSetupPayload,
    'workspacePreferences.aiSuggestionsEnabled': draft.aiSuggestionsEnabled ?? true,
    'workspacePreferences.notificationsEnabled': draft.notificationsEnabled ?? true,
    'workspacePreferences.gamificationEnabled': true,
    enabledDashboardViews: views.enabledDashboardViews,
    defaultDashboardView: views.defaultDashboardView,
    modules,
  })

  // Set the notification master policy
  const masterEnabled = draft.notificationsEnabled ?? true
  await saveWorkspaceNotificationPreferences(
    workspaceId,
    'ALL_MEMBERS',
    {
      ...createDefaultWorkspaceNotificationPreferences(workspaceId, 'ALL_MEMBERS'),
      masterEnabled,
    },
  )

  return views
}

export interface WorkspaceProfilePatch {
  roleTitle?: string | null
  useCase?: ExtendedWorkspaceUseCase | null
  audience?: 'SOLO' | 'SOLO_HELPERS' | 'TEAM' | null
  teamSize?: string | null
  focusAreas?: string[] | null
  intentText?: string | null
}

export async function updateWorkspaceProfile(
  workspaceId: string,
  patch: WorkspaceProfilePatch,
): Promise<void> {
  if (patch.intentText && patch.intentText.length > 280) {
    throw new Error('Warm-up intent sentence must not exceed 280 characters.')
  }

  const payload: Record<string, unknown> = {}
  if (patch.roleTitle !== undefined) {
    payload['onboardingSetup.roleTitle'] = patch.roleTitle || null
  }
  if (patch.useCase !== undefined) {
    payload['onboardingSetup.useCase'] = patch.useCase || null
  }
  if (patch.audience !== undefined) {
    payload['onboardingSetup.audience'] = patch.audience || null
  }
  if (patch.teamSize !== undefined) {
    payload['onboardingSetup.teamSize'] = patch.teamSize || null
  }
  if (patch.focusAreas !== undefined) {
    payload['onboardingSetup.focusAreas'] = patch.focusAreas || []
  }
  if (patch.intentText !== undefined) {
    payload['onboardingSetup.intentText'] = patch.intentText || null
  }

  await updateDoc(doc(firestoreDb, 'workspaces', workspaceId), payload)
}

export async function setAiSuggestionsEnabled(
  workspaceId: string,
  enabled: boolean,
): Promise<void> {
  await updateDoc(doc(firestoreDb, 'workspaces', workspaceId), {
    'workspacePreferences.aiSuggestionsEnabled': enabled,
    'onboardingSetup.preferences.aiSuggestionsEnabled': enabled,
  })
}

